import {
  PublicClientApplication,
  AccountInfo,
  AuthenticationResult,
  InteractionRequiredAuthError,
  BrowserAuthError,
  Configuration,
} from "@azure/msal-browser";
import store from "@/store/index";

class MsalService {
  private msalConfig: Configuration | null = null;

  private scopes: string[] | null = null;

  private instance: PublicClientApplication | null = null;

  private initialized = false;

  public async initialize() {
    if (this.initialized) return;

    this.msalConfig = {
      auth: {
        clientId: store.getters.authenticationConfiguration.clientId,
        authority: store.getters.authenticationConfiguration.authority,
        redirectUri: store.getters.authenticationConfiguration.redirectUri,
        postLogoutRedirectUri:
          store.getters.authenticationConfiguration.redirectUri,
        navigateToLoginRequestUrl: true,
      },
      cache: {
        cacheLocation: "localStorage",
        storeAuthStateInCookie: false,
      },
    };

    this.scopes = store.getters.authenticationConfiguration.scopes;

    this.instance = new PublicClientApplication(this.msalConfig);
    await this.instance.handleRedirectPromise();
    this.initialized = true;
  }

  loginRedirect() {
    if (this.instance!.getAllAccounts().length === 0) {
      this.instance!.loginRedirect({
        scopes: this.scopes!, // API scope
      });
    }
  }

  logout() {
    this.instance!.logoutRedirect();
  }

  getAccount(): AccountInfo | null {
    const accounts = this.instance!.getAllAccounts();
    return accounts.length ? accounts[0] : null;
  }

  async getToken(): Promise<string | null> {
    const account = this.getAccount();
    if (!account) return null;

    try {
      const result: AuthenticationResult = await this.instance!.acquireTokenSilent(
        {
          scopes: this.scopes!,
          account,
        },
      );

      return result.accessToken;
    } catch (err) {
      console.warn("Silent token refresh failed, doing redirect", err);

      const isInteractionRequired =
        err instanceof InteractionRequiredAuthError ||
        (err as BrowserAuthError).errorCode === "monitor_window_timeout" ||
        (err as BrowserAuthError).errorCode === "interaction_required" ||
        (err as BrowserAuthError).errorCode === "login_required";

      if (isInteractionRequired) {
        // fallback to redirect
        await this.instance!.acquireTokenRedirect({
          scopes: this.scopes!,
        });
        return null;
      }
      throw err;
    }
  }
}

export const msalService = new MsalService();
